import React, { useEffect, useRef, useState } from 'react';
import { ErrorInput } from '@fx-ui/jdy-design-biz';
import { Dialog, DialogProps, Input, Message, Drawer, Button } from '@fx-ui/fine-design';
import {
    get,
    getApi,
    includes,
    isEmpty,
    isMobileUA,
    isNil,
    map,
    post,
    size,
    trim,
    verifyPhone
} from '~/utils';
import Intl from '@fx-ui/intl';
import { API, ECode, EMPTY_FN, TEXT_TYPE_REGEXP, UGC_MAX_LENGTH } from '~/constants';
import { IUserProfile, VALID_STEP, VALID_TYPE, VERIFY_MODE } from '~/interfaces';
import { CaptchaDialog, ValidCode, I18nPhone } from '@fx-ui/platform-sdk';

interface VerifyChangePaneProps extends Pick<DialogProps, 'title' | 'text4Ok'> {
    verifyMode: VERIFY_MODE;
    mode: VALID_STEP; // 验证步骤
    validType: VALID_TYPE; // 验证内容
    setValidType: (validType: VALID_TYPE) => void;
    setCodeValid: React.Dispatch<React.SetStateAction<boolean>>;
    userInfo: Partial<IUserProfile>;
    codeValid: boolean;
    visible: boolean;
    onClose?: () => void;
    availableValidateType: string[];
    onModifySuccess: (
        phoneValue: string,
        value: string // 验证码或者密码
    ) => void | Promise<any>;
    supportMobileLayout?: boolean; // 是否需要支持移动端布局
    /** 验证表单上方的业务说明。 */
    description?: React.ReactNode;
}

const getDialogTitle = (verifyMode: VERIFY_MODE, mode: VALID_STEP) => {
    const isEmail = verifyMode === VERIFY_MODE.EMAIL;
    switch (mode) {
        case VALID_STEP.BIND:
        case VALID_STEP.BIND2:
            return isEmail ? Intl.get('email.bind') : Intl.get('phone.bind');
        case VALID_STEP.UNBIND:
            return isEmail ? Intl.get('email.unbind') : Intl.get('phone.unbind');
        case VALID_STEP.VERIFY:
            return isEmail ? Intl.get('email.verify') : Intl.get('phone.verify');
        case VALID_STEP.VERIFY2:
            return Intl.get('password.set');
        case VALID_STEP.MFA:
            return Intl.get('account.signin.validCode.emptyTip');
        case VALID_STEP.MODIFY:
        case VALID_STEP.MODIFY2:
            return isEmail ? Intl.get('email.update') : Intl.get('phone.update');
        case VALID_STEP.EXIT:
            return Intl.get('authentication');
        default:
            return '';
    }
};

const codeVerify = (codeValue: string) => {
    return !!codeValue;
};

const passwordVerify = (password: string) => {
    return password.length >= 6;
};

const hasType = (type: 'pwd' | 'email' | 'phone', availableValidateType: string[]) => {
    return includes(availableValidateType, type);
};

const { EMAIL } = UGC_MAX_LENGTH.USER;

const VerifyChangePane: React.FunctionComponent<VerifyChangePaneProps> = (props) => {
    const {
        verifyMode,
        mode,
        userInfo,
        validType,
        visible,
        codeValid,
        onClose = EMPTY_FN,
        setValidType,
        setCodeValid,
        availableValidateType,
        onModifySuccess,
        supportMobileLayout,
        description,
        title,
        text4Ok
    } = props;
    const phoneDelimiter = '-';
    const { email, phone = '', is_email_verified, is_phone_verified } = userInfo;
    const [phoneValid, setPhoneValid] = useState<boolean>(true);
    const [emailValid, setEmailValid] = useState<boolean>(true);
    const [phoneValue, setPhoneValue] = useState<string>('');
    const [emailValue, setEmailValue] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [isPasswordValid, setPasswordValid] = useState<boolean>(true);
    const [codeValue, setCodeValue] = useState<string>('');
    const [captchaDialogVisible, setCaptchaDialogVisible] = useState<boolean>(false); // 是否显示图片验证码
    const [captchaData, setCaptchaData] = useState<Record<string, string>>({ phone }); // 图片验证码手机号参数
    const validCodeRef = useRef<any>();
    const isEmailOperate = verifyMode === VERIFY_MODE.EMAIL;
    const isMobile = supportMobileLayout && isMobileUA(); // 是否是移动端布局

    useEffect(() => {
        // 跳到下一步时清除倒计时状态
        if (validCodeRef.current) {
            validCodeRef.current.clearReloadState();
        }
        setCodeValue('');
        setPhoneValue('');
        setEmailValue('');
        setPassword('');
        setCodeValid(true);
    }, [mode, validType, visible]);

    useEffect(() => {
        if (validType === VALID_TYPE.EMAIL) {
            setCaptchaData({ email: emailValue });
        }
    }, [emailValue]);

    useEffect(() => {
        if (validType === VALID_TYPE.PHONE) {
            setCaptchaData({ phone: phoneValue });
        }
    }, [phoneValue]);

    // 密码校验途径
    const changeToPwd = () => {
        return (
            hasType('pwd', availableValidateType) && (
                <div className="line-footer">
                    <span>
                        {isEmailOperate
                            ? Intl.get('phone.disable.question')
                            : Intl.get('email.disable.question')}
                    </span>
                    <span
                        onClick={() => {
                            setValidType(VALID_TYPE.PWD);
                        }}
                        className="text-primary"
                    >
                        {mode === VALID_STEP.UNBIND
                            ? Intl.get('unbind.by.pwd')
                            : Intl.get('password.validate')}
                    </span>
                </div>
            )
        );
    };

    // 候选验证途径
    const renderDoubleChange = () => {
        return (
            !isEmpty(availableValidateType) && (
                <div className="line-footer">
                    <span>
                        {size(availableValidateType) > 1 &&
                            (isEmailOperate
                                ? Intl.get('email.disable.question')
                                : Intl.get('phone.disable.question'))}
                    </span>
                    {hasType('pwd', availableValidateType) && (
                        <span
                            onClick={() => {
                                setValidType(VALID_TYPE.PWD);
                            }}
                            className="text-primary"
                        >
                            {mode === VALID_STEP.UNBIND
                                ? Intl.get('unbind.by.pwd')
                                : Intl.get('password.validate')}
                        </span>
                    )}
                    {size(availableValidateType) > 2 && ' | '}
                    {hasType('email', availableValidateType) && !isEmailOperate && (
                        <span
                            onClick={() => {
                                setValidType(VALID_TYPE.EMAIL);
                            }}
                            className="text-primary"
                        >
                            {mode === VALID_STEP.UNBIND
                                ? Intl.get('unbind.by.email')
                                : Intl.get('email.validate')}
                        </span>
                    )}
                    {hasType('phone', availableValidateType) && isEmailOperate && (
                        <span
                            onClick={() => {
                                setValidType(VALID_TYPE.PHONE);
                            }}
                            className="text-primary"
                        >
                            {mode === VALID_STEP.UNBIND
                                ? Intl.get('unbind.by.phone')
                                : Intl.get('phone.validate')}
                        </span>
                    )}
                </div>
            )
        );
    };

    const emailVerify = () => {
        return new RegExp(TEXT_TYPE_REGEXP.EMAIL).test(emailValue);
    };

    const emailOverLimit = () => {
        return size(trim(emailValue)) > EMAIL;
    };

    // dialog footer
    const renderFooter = () => {
        switch (mode) {
            case VALID_STEP.BIND: {
                const className = isMobile ? 'text-blue' : 'text-primary';
                if (
                    includes([VALID_TYPE.EMAIL, VALID_TYPE.PHONE], validType) &&
                    includes(availableValidateType, 'pwd')
                ) {
                    return (
                        <div className="line-footer">
                            <span>
                                {validType === VALID_TYPE.PHONE
                                    ? Intl.get('phone.disable.question')
                                    : Intl.get('email.disable.question')}
                            </span>
                            <span
                                onClick={() => {
                                    setValidType(VALID_TYPE.PWD);
                                }}
                                className={className}
                            >
                                {Intl.get('password.validate')}
                            </span>
                        </div>
                    );
                }
                if (
                    isMobile &&
                    validType === VALID_TYPE.PWD &&
                    includes(availableValidateType, VALID_TYPE.EMAIL)
                ) {
                    return (
                        <div className="line-footer">
                            <span>{Intl.get('password.forget')}</span>
                            <span
                                onClick={() => {
                                    setValidType(VALID_TYPE.EMAIL);
                                }}
                                className={className}
                            >
                                {Intl.get('code.validate')}
                            </span>
                        </div>
                    );
                }
                break;
            }
            case VALID_STEP.UNBIND:
            case VALID_STEP.MODIFY:
                switch (validType) {
                    case VALID_TYPE.PHONE:
                        if (isEmailOperate) {
                            return changeToPwd();
                        } else {
                            return renderDoubleChange();
                        }
                    case VALID_TYPE.PWD:
                        if (
                            (verifyMode === VERIFY_MODE.PHONE && !is_phone_verified) ||
                            (verifyMode === VERIFY_MODE.EMAIL && !is_email_verified)
                        ) {
                            // 绑定但未验证场景，进行对侧验证时，仅支持单向切到密码验证，不支持切回
                            return null;
                        }
                        return (
                            ((hasType('phone', availableValidateType) && isEmailOperate) ||
                                (hasType('email', availableValidateType) && !isEmailOperate)) && (
                                <div className="line-footer">
                                    <span>{Intl.get('password.forget')}</span>
                                    <span
                                        onClick={() => {
                                            setValidType(
                                                isEmailOperate ? VALID_TYPE.PHONE : VALID_TYPE.EMAIL
                                            );
                                        }}
                                        className="text-primary"
                                    >
                                        {isEmailOperate &&
                                            (mode === VALID_STEP.UNBIND
                                                ? Intl.get('unbind.by.phone')
                                                : Intl.get('phone.validate'))}
                                        {!isEmailOperate &&
                                            (mode === VALID_STEP.UNBIND
                                                ? Intl.get('unbind.by.email')
                                                : Intl.get('email.validate'))}
                                    </span>
                                </div>
                            )
                        );
                    case VALID_TYPE.EMAIL:
                        if (!isEmailOperate) {
                            return changeToPwd();
                        } else {
                            return renderDoubleChange();
                        }
                }
                break;
            case VALID_STEP.VERIFY2:
                if (includes([VALID_TYPE.EMAIL, VALID_TYPE.PHONE], validType)) {
                    const renderVerify2Footer = () => {
                        if (validType === VALID_TYPE.PHONE && is_email_verified) {
                            return (
                                <>
                                    <span>{Intl.get('phone.disable.question')}</span>
                                    <span
                                        className="text-primary"
                                        onClick={() => {
                                            setValidType(VALID_TYPE.EMAIL);
                                        }}
                                    >
                                        {Intl.get('email.validate')}
                                    </span>
                                </>
                            );
                        } else if (validType === VALID_TYPE.EMAIL && is_phone_verified) {
                            return (
                                <>
                                    <span> {Intl.get('email.disable.question')}</span>
                                    <span
                                        className="text-primary"
                                        onClick={() => {
                                            setValidType(VALID_TYPE.PHONE);
                                        }}
                                    >
                                        {Intl.get('phone.validate')}
                                    </span>
                                </>
                            );
                        }
                        return null;
                    };

                    return <div className="line-footer">{renderVerify2Footer()}</div>;
                }
                break;
            default:
                return null;
        }
    };

    // 根据类型和步骤加载内容
    const renderContentArrayByType = () => {
        switch (mode) {
            case VALID_STEP.BIND:
                switch (validType) {
                    case VALID_TYPE.EMAIL:
                        return ['tip', 'email_disabled', 'email_validate'];
                    case VALID_TYPE.PWD:
                        if (hasType('email', availableValidateType) && !isEmailOperate) {
                            return ['tip', 'email_disabled', 'pwd'];
                        } else if (hasType('phone', availableValidateType) && isEmailOperate) {
                            return ['tip', 'phone_disabled', 'pwd'];
                        } else {
                            return ['tip', 'pwd'];
                        }
                    case VALID_TYPE.PHONE:
                        return ['tip', 'phone_disabled', 'phone_validate'];
                }
                break;
            case VALID_STEP.UNBIND:
                switch (validType) {
                    case 'email':
                        return ['email_disabled', 'email_validate'];
                    case 'pwd':
                        return [!isEmailOperate ? 'phone_disabled' : 'email_disabled', 'pwd'];
                    case 'phone':
                        return ['phone_disabled', 'phone_validate'];
                }
                break;
            case VALID_STEP.VERIFY:
                switch (validType) {
                    case 'email':
                        return ['email_disabled', 'email_validate'];
                    case 'phone':
                        return ['phone_disabled', 'phone_validate'];
                }
                break;
            case VALID_STEP.VERIFY2:
                switch (validType) {
                    case 'email':
                        return ['tip', 'email_disabled', 'email_validate'];
                    case 'phone':
                        return ['tip', 'phone_disabled', 'phone_validate'];
                }
                break;
            case VALID_STEP.MODIFY:
                switch (validType) {
                    case 'email':
                        return ['tip', 'email_disabled', 'email_validate'];
                    case 'pwd':
                        return [
                            'tip',
                            !isEmailOperate ? 'phone_disabled' : 'email_disabled',
                            'pwd'
                        ];
                    case 'phone':
                        return ['tip', 'phone_disabled', 'phone_validate'];
                }
                break;
            case VALID_STEP.BIND2:
            case VALID_STEP.MODIFY2:
                switch (validType) {
                    case 'email':
                        return ['email', 'email_validate'];
                    case 'phone':
                        return ['phone', 'phone_validate'];
                }
                break;
            case VALID_STEP.MFA:
                switch (validType) {
                    case 'email':
                        return ['tip', 'email_disabled', 'email_validate'];
                    case 'phone':
                        return ['tip', 'phone_disabled', 'phone_validate'];
                }
                break;
            case VALID_STEP.EXIT:
                switch (validType) {
                    case 'email':
                        return ['email_disabled', 'email_validate'];
                    case 'phone':
                        return ['phone_disabled', 'phone_validate'];
                }
                break;
        }
    };

    /**
     * 验证码点击
     *
     * @param validType
     */
    const dealValidCodeBtnClick = (validType: 'email' | 'phone') => {
        if (validType === 'phone') {
            if (!includes([VALID_STEP.BIND2, VALID_STEP.MODIFY2], mode)) {
                if (isEmpty(phone)) {
                    warningToast(Intl.get('phone.invalid.warning'));
                    return;
                }
                const data: any = {
                    phone: phone
                };
                if (mode !== VALID_STEP.MFA) {
                    data.check_current_phone = true;
                }
                return post(getApi(API.authentication.send_sms), data, {
                    customHandleErrorCodes: [ECode.USER_NO_AUTH_CODE_CAPTCHA]
                })
                    .then(() => {
                        validCodeRef.current.setReloadState();
                    })
                    .catch((err) => {
                        const errCode = get(err, 'response.data.code');
                        if (ECode.USER_NO_AUTH_CODE_CAPTCHA === errCode) {
                            setCaptchaData({ phone: data.phone });
                            setCaptchaDialogVisible(true);
                        }
                    });
            }
            if (!phoneValue) {
                warningToast(Intl.get('phone.invalid.warning'));
                return;
            }
            // 是否校验当前手机
            const checkCurrentPhone = mode === 'unbind' || mode === 'modify';
            const data: any = {
                phone: phoneValue
            };
            if (checkCurrentPhone) {
                data.check_current_phone = !isEmpty(phone);
            }
            return post(getApi(API.authentication.send_sms), data, {
                customHandleErrorCodes: [ECode.USER_NO_AUTH_CODE_CAPTCHA]
            })
                .then(() => {
                    validCodeRef.current.setReloadState();
                })
                .catch((err) => {
                    const errCode = get(err, 'response.data.code');
                    if (ECode.USER_NO_AUTH_CODE_CAPTCHA === errCode) {
                        setCaptchaData({ phone: data.phone });
                        setCaptchaDialogVisible(true);
                    }
                });
        } else {
            if (!includes([VALID_STEP.BIND2, VALID_STEP.MODIFY2], mode)) {
                if (isEmpty(email)) {
                    warningToast(Intl.get('email.invalid.warning'));
                    return;
                }
                const data = {
                    email: email,
                    check_current_email: is_email_verified
                };
                return post(getApi(API.authentication.send_email), data, {
                    customHandleErrorCodes: [ECode.USER_NO_AUTH_CODE_CAPTCHA]
                })
                    .then(() => {
                        validCodeRef.current.setReloadState();
                    })
                    .catch((err) => {
                        const errCode = get(err, 'response.data.code');
                        if (ECode.USER_NO_AUTH_CODE_CAPTCHA === errCode) {
                            setCaptchaData({ email: data.email || '' });
                            setCaptchaDialogVisible(true);
                        }
                    });
            }
            if (!emailValue) {
                warningToast(Intl.get('email.invalid.warning'));
                return;
            }
            if (emailOverLimit()) {
                return;
            }

            return post(
                getApi(API.authentication.send_email),
                { email: emailValue },
                { customHandleErrorCodes: [ECode.USER_NO_AUTH_CODE_CAPTCHA] }
            )
                .then(() => {
                    validCodeRef.current.setReloadState();
                })
                .catch((err) => {
                    const errCode = get(err, 'response.data.code');
                    if (ECode.USER_NO_AUTH_CODE_CAPTCHA === errCode) {
                        setCaptchaData({ email: emailValue });
                        setCaptchaDialogVisible(true);
                    }
                });
        }
    };

    const getText4Ok = () => {
        switch (mode) {
            case VALID_STEP.MODIFY:
            case VALID_STEP.BIND:
            case VALID_STEP.VERIFY2:
                return Intl.get('nextStep');
            case VALID_STEP.UNBIND:
                return Intl.get('unbind');
            case VALID_STEP.MFA:
                return Intl.get('ok');
            case VALID_STEP.EXIT:
                return Intl.get('member.exit.corp.ensure');
            default:
                return Intl.get('save');
        }
    };

    const getText4Cancel = () => {
        switch (mode) {
            case VALID_STEP.EXIT:
                return Intl.get('member.exit.corp.not.yet');
            default:
                return Intl.get('cancel');
        }
    };

    const getProps4OK = () => {
        switch (mode) {
            case VALID_STEP.EXIT:
                return {
                    loadingWhenClick: true,
                    danger: true
                };
            default:
                return undefined;
        }
    };

    const warningToast = (text: string) => {
        Message.add({
            messageType: 'toast',
            type: 'warning',
            message: text
        });
    };

    const handleDialogOnOk = () => {
        switch (validType) {
            case VALID_TYPE.PHONE:
                if (
                    includes([VALID_STEP.MODIFY2, VALID_STEP.BIND2], mode) &&
                    !verifyPhone(phoneValue, true)
                ) {
                    setPhoneValid(false);
                    warningToast(Intl.get('phone.invalid.warning'));
                    break;
                }
                setCodeValid(codeVerify(codeValue));
                if (isEmpty(codeValue)) {
                    warningToast(Intl.get('phone.check.required'));
                } else {
                    return onModifySuccess(phoneValue, codeValue);
                }
                break;
            case VALID_TYPE.EMAIL:
                if (includes([VALID_STEP.MODIFY2, VALID_STEP.BIND2], mode)) {
                    if (emailOverLimit()) {
                        break;
                    } else if (!emailVerify()) {
                        setEmailValid(false);
                        warningToast(Intl.get('email.invalid.warning'));
                        break;
                    }
                }
                setCodeValid(codeVerify(codeValue));
                if (isEmpty(codeValue)) {
                    warningToast(Intl.get('email.check.required'));
                    break;
                }
                return onModifySuccess(emailValue, codeValue);
            case VALID_TYPE.PWD:
                setPasswordValid(passwordVerify(password));
                if (isEmpty(password)) {
                    warningToast(Intl.get('account.signin.password.emptyTip'));
                    break;
                }
                return onModifySuccess('', password);
        }
    };

    const renderLineContent = (type: string, i: number) => {
        const inputHeight = isMobile ? 40 : 32;
        switch (type) {
            case 'tip':
                return (
                    <div key={i} className="line-content">
                        <span className="line-content-tip">
                            {description ??
                                (mode === VALID_STEP.MFA
                                    ? Intl.get('login.mfa.desc')
                                    : Intl.get('safe.verify.check'))}
                        </span>
                    </div>
                );
            case 'email':
                return (
                    <div key={i} className="line-content">
                        <div className="line-label">{Intl.get('email.new')}</div>
                        <ErrorInput
                            errMsg={
                                emailOverLimit()
                                    ? Intl.get('max.length.error.message', { max: EMAIL })
                                    : ''
                            }
                            style={{ width: '100%', height: `${inputHeight}px` }}
                            value={emailValue}
                            inputState={!emailOverLimit() && emailValid ? 'normal' : 'error'}
                            onChange={(value) => {
                                setEmailValue(value);
                            }}
                            onBlur={() => {
                                setEmailValid(emailVerify());
                            }}
                        />
                    </div>
                );
            case 'email_disabled':
                return (
                    <div key={i} className="line-content">
                        <div className="line-label">{Intl.get('email.current')}</div>
                        <Input
                            style={{ width: '100%', height: `${inputHeight}px` }}
                            value={email}
                            disabled
                        />
                    </div>
                );
            case 'email_validate':
            case 'phone_validate':
                return (
                    <div key={i} className="line-content">
                        <div className="line-label">{Intl.get('verify.code')}</div>
                        <ValidCode
                            ref={validCodeRef}
                            codeValid={codeValid}
                            onChange={(value) => {
                                setCodeValue(value);
                            }}
                            waterMark={
                                type === 'email_validate'
                                    ? Intl.get('email.verify.code')
                                    : Intl.get('phone.verify.code')
                            }
                            onBtnClick={() => {
                                dealValidCodeBtnClick(
                                    type === 'email_validate' ? 'email' : 'phone'
                                );
                            }}
                            onInputBlur={() => {
                                setCodeValid(!!codeValue);
                            }}
                            height={inputHeight}
                        />
                    </div>
                );
            case 'pwd':
                return (
                    <div key={i} className="line-content">
                        <div className="line-label">{Intl.get('password.signIn')}</div>
                        <Input
                            className="password-input"
                            style={{ width: '100%', height: `${inputHeight}px` }}
                            value={password}
                            type="password"
                            inputState={isPasswordValid ? 'normal' : 'error'}
                            onChange={(val) => {
                                setPassword(val);
                            }}
                            onBlur={(v) => {
                                setPasswordValid(passwordVerify(v));
                            }}
                            placeholder={Intl.get('password')}
                        />
                    </div>
                );
            case 'phone':
                return (
                    <div key={i} className="line-content">
                        <div className="line-label">{Intl.get('phone.new')}</div>
                        <I18nPhone
                            phoneValid={phoneValid}
                            disabled={false}
                            value={phoneValue}
                            height={inputHeight}
                            phoneDelimiter={phoneDelimiter}
                            onBlur={(value) => {
                                setPhoneValid(verifyPhone(value, true));
                                setPhoneValue(value);
                            }}
                        />
                    </div>
                );
            case 'phone_disabled':
                return (
                    <div key={i} className="line-content">
                        <div className="line-label">{Intl.get('phone.current')}</div>
                        <I18nPhone
                            phoneValid={true}
                            disabled={true}
                            value={phone}
                            height={inputHeight}
                            phoneDelimiter={phoneDelimiter}
                        />
                    </div>
                );
        }
    };

    return (
        <>
            {isMobile ? (
                <Drawer
                    className="fx-operate-drawer"
                    visible={visible}
                    title={getDialogTitle(verifyMode, mode)}
                    onClose={() => {
                        onClose();
                    }}
                    edge={{ height: 400 }}
                    closable={false}
                    autoClose
                    placement="bottom"
                >
                    {map(renderContentArrayByType(), (v, i) => {
                        return renderLineContent(v, i);
                    })}
                    {renderFooter()}
                    <div className="fx-operate-drawer-footer">
                        <Button className="drawer-btn btn-cancel" onClick={onClose}>
                            {Intl.get('cancel')}
                        </Button>
                        <Button className="drawer-btn btn-ok" onClick={handleDialogOnOk}>
                            {getText4Ok()}
                        </Button>
                    </div>
                </Drawer>
            ) : (
                <Dialog
                    className="fx-operate-dialog"
                    title={title ?? getDialogTitle(verifyMode, mode)}
                    visible={visible}
                    width={500}
                    height={362}
                    text4Cancel={getText4Cancel()}
                    props4Ok={getProps4OK()}
                    text4Ok={text4Ok ?? getText4Ok()}
                    separator={{ footer: false }}
                    footer={renderFooter()}
                    onCancel={() => {
                        onClose();
                    }}
                    onOk={() => {
                        return handleDialogOnOk();
                    }}
                >
                    {map(renderContentArrayByType(), (v, i) => {
                        return renderLineContent(v, i);
                    })}
                </Dialog>
            )}
            <CaptchaDialog
                visible={captchaDialogVisible}
                url={
                    !isNil(captchaData.email)
                        ? getApi(API.authentication.send_email)
                        : getApi(API.authentication.send_sms)
                }
                data={captchaData}
                onClose={() => {
                    setCaptchaDialogVisible(false);
                }}
                onSuccess={() => {
                    setCaptchaDialogVisible(false);
                    validCodeRef.current.setReloadState();
                }}
            />
        </>
    );
};

export default VerifyChangePane;
